import classNames from 'classnames';
import React, { useEffect, useState, useContext } from 'react';
import { State } from '../../contexts/GlobalState';
import { createUseStyles } from 'react-jss';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/ArrowDownIcon.svg';
import { ReactComponent as BackArrowIcon } from '../../assets/icons/BackArrowIcon.svg';
import variables from '../../theme/variables';
import Lottie from '../common/Lottie';
import TransitionText from '../common/TransitionText';

const useStyles = createUseStyles(
    {
        wrap: {
            position: 'relative',
            top: 0,
            left: 0,
            width: '100%',
            minHeight: 'var(--app-height)',
            transition: 'opacity 0.5s ease-in-out',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'stretch',
        },
        background: {
            width: '120vw',
            height: '120vh',
            position: 'fixed',
            top: '-10vh',
            left: '-10vw',
            top: 0,
            left: 0,
            zIndex: -1,
        },
        closeBtnWrap: {
            position: 'absolute',
            height: 'calc(100% - 100px)',
            left: 14,
            top: 21 + variables.msftHeaderHeightMobile,

            '@media (min-width: 860px)': {
                top: 21 + variables.msftHeaderHeight,
            },

            '@media (min-width: 1024px)': {
                left: 60,
                top: 45 + variables.msftHeaderHeight,
            },
        },
        closeBtn: {
            position: 'sticky',
            left: 14,
            top: 21,

            '@media (min-width: 860px)': {
                top: 21 + variables.msftHeaderHeight,
            },

            '@media (min-width: 1024px)': {
                left: 60,
                top: 45,
            },

            background: 'rgba(255, 255, 255, 0.16)',
            border: '1px solid #CCCCCC',
            backdropFilter: 'blur(76px)',
            borderRadius: 8,
            color: variables.palette.white,
            fontSize: 18,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            height: 52,
            cursor: 'pointer',
            zIndex: 3000,

            '& svg': {
                marginRight: 8,
            },
        },

        introWrap: {
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            paddingTop: 80,
            paddingBottom: 80,
        },

        nextButtonWrap: {
            position: 'absolute',
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
        },

        nextButton: {
            width: 48,
            height: 48,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            transition: 'opacity 0.2s ease-out',
            transitionDelay: '1000ms',
        },

        animationWrapLandscape: {
            position: 'absolute',
            zIndex: -1,
            aspectRatio: 2880 / 2048,

            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            height: `var(--app-height)`,
            width: 'auto',

            ['@media (min-aspect-ratio: 2880/2048)']: {
                width: '100%',
                height: 'auto',
            },

            ['@media (orientation: portrait)']: {
                display: 'none',
            },
        },

        animationWrapPortrait: {
            position: 'absolute',
            zIndex: -1,
            aspectRatio: 1080 / 1920,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',

            height: `calc(var(--app-height) + 300px)`,
            width: 'auto',

            ['@media (min-aspect-ratio: 1080/1920)']: {
                width: '150%',
                height: 'auto',
            },

            ['@media (orientation: landscape)']: {
                display: 'none',
            },
        },

        introContent: {
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 32px',
            textAlign: 'center',
        },

        introTitle: {
            fontSize: 48,
            lineHeight: '52px',
            letterSpacing: '-1px',
            margin: '0 auto 20px',

            '@media (min-width: 1024px)': {
                fontSize: 80,
                lineHeight: '84px',
                letterSpacing: '-2px',
                marginBottom: 45,
            },
        },

        introParagraph: {
            maxWidth: 540,
            fontSize: 16,
            lineHeight: '22px',
            padding: '0 16px',

            '& p:not(:last-of-type)': {
                marginBottom: '1em',
            },

            '@media (min-width: 1024px)': {
                fontSize: 18,
                lineHeight: '28px',
            },
        },
    },
    { name: 'PrimaryWrapper' }
);

export default function PrimaryWrapper({ onClose, data }) {
    const classes = useStyles();
    const { returnButtonTheme, setReturnButtonTheme } = useContext(State);

    const [state, setState] = useState('init'); // init, intro, interactive, leave
    const [titleState, setTitleState] = useState('init'); // init | in | leave
    const [animationPaused, setAnimationPaused] = useState(false);
    const [nextButtonState, setNextButtonState] = useState('init'); // init | in | leave

    useEffect(() => {
        setReturnButtonTheme(data.buttonTheme);
        setTimeout(() => setState('intro'), 1);
    }, []);

    const handleClose = () => {
        setState('leave');
        setTimeout(onClose, 510);
    };

    const handleNext = () => {
        setAnimationPaused(false);
        setTitleState('leave');
        setNextButtonState('leave');
        setTimeout(() => setState('interactive'), 1800);
    };

    return (
        <div
            className={classNames(classes.wrap)}
            style={{
                opacity: ['intro', 'interactive'].includes(state) ? 1 : 0,
                color: data.color,
            }}
            id="primaryWrapper"
        >
            <div
                className={classes.background}
                style={{
                    backgroundColor: data.backgroundColor,
                }}
            ></div>
            <div className={classes.closeBtnWrap}>
                <button
                    className={classes.closeBtn}
                    onClick={handleClose}
                    style={{ ...returnButtonTheme }}
                >
                    <BackArrowIcon />
                    &nbsp;Studio
                </button>
            </div>

            {state === 'intro' && (
                <div className={classes.introWrap}>
                    <div className={classes.animationWrapLandscape}>
                        <Lottie
                            speed={2.5}
                            width={'100%'}
                            height={'100%'}
                            // animationData={data.introAnimation}
                            path={data.introAnimation}
                            loop={false}
                            paused={animationPaused}
                            onFrame={[
                                {
                                    frame: 190,
                                    callback: () => {
                                        setTitleState('in');
                                        setNextButtonState('in');
                                    },
                                },
                                {
                                    frame: 230,
                                    callback: () => {
                                        setAnimationPaused(true);
                                    },
                                },
                            ]}
                        />
                    </div>
                    <div className={classes.animationWrapPortrait}>
                        <Lottie
                            speed={2.5}
                            width={'100%'}
                            height={'100%'}
                            // animationData={data.introAnimationMobile}
                            path={data.introAnimationMobile}
                            loop={false}
                            paused={animationPaused}
                        />
                    </div>
                    <div className={classes.introContent}>
                        <TransitionText
                            delay={0}
                            state={titleState}
                            easing="cubic-bezier(0.16, 1, 0.3, 1)" // ease-out-quint
                            duration={1500}
                        >
                            <h2
                                className={classes.introTitle}
                                dangerouslySetInnerHTML={{ __html: data.title }}
                            ></h2>
                        </TransitionText>
                        <TransitionText
                            delay={100}
                            state={titleState}
                            easing="cubic-bezier(0.16, 1, 0.3, 1)" // ease-out-quint
                            duration={1500}
                        >
                            <div
                                className={classes.introParagraph}
                                dangerouslySetInnerHTML={{
                                    __html: data.subtitle,
                                }}
                            ></div>
                        </TransitionText>
                    </div>

                    <div className={classes.nextButtonWrap}>
                        <TransitionText
                            delay={200}
                            state={nextButtonState}
                            easing="cubic-bezier(0.16, 1, 0.3, 1)" // ease-out-quint
                            duration={1500}
                        >
                            <button
                                className={classes.nextButton}
                                onClick={handleNext}
                                style={{
                                    // opacity: nextButtonVisible ? 1 : 0,
                                    // visibility: nextButtonVisible
                                    //     ? 'visible'
                                    //     : 'hidden',
                                    color: data.color,
                                }}
                            >
                                <ArrowDownIcon />
                            </button>
                        </TransitionText>
                    </div>
                </div>
            )}

            {state === 'interactive' && data.component}
        </div>
    );
}
